/**
 * This composable manages a user's favourited RVs and collections.
 *
 * Question: why do we have separate endpoints for different methods?
 * I.e.: `me/update-collection` instead of `me/collection` with a PUT method.
 */
export default function useFavouritesData() {
  const { $rentals, $captureError } = useNuxtApp()
  const { isLoggedIn } = useAuthentication()
  const { userId } = useUser()

  const favourites = useFavourites()
  const isLoading = ref(false)

  /**
   * Makes sure that we're currently not already running another action.
   */
  function passesLoadingGuard() {
    return !isLoading.value
  }

  /**
   * Makes sure that the user is logged in.
   */
  function passesLoggedInGuard() {
    return isLoggedIn.value
  }

  /**
   * Makes sure that the user is logged in and that we're currently not already
   * running another action.
   */
  function passesAllGuards() {
    return passesLoadingGuard() && passesLoggedInGuard()
  }

  /**
   * Fetches the user's favourites from the API.
   */
  function getFavourites() {
    return $rentals('/api/user-profiles/me/favourites')
  }

  /**
   * Fetches and stores the user's favourites.
   */
  async function getAndSetFavourites() {
    if (!passesLoggedInGuard()) {
      return
    }

    try {
      isLoading.value = true

      const favs = await getFavourites()

      if (favs) {
        favourites.value = favs.map((collection) => ({
          ...collection,
          Items: collection.Items?.filter((item) => item.RV) ?? [],
        }))
      }

      return favs
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Clears the user's favourites.
   */
  function resetFavourites() {
    favourites.value = []
  }

  /**
   * Adds an RV to a collection, or adds it to the default collection if no
   * collection ID is provided.
   *
   * @param RvId The ID of the RV to add to the collection
   * @param CollectionId The ID of the collection to add the RV to
   * @returns A promise that resolves when the RV is added to the collection
   */
  async function addRvToCollection(RvId: string, CollectionId?: number) {
    if (!passesAllGuards()) {
      return
    }

    try {
      isLoading.value = true

      const collectionListId = CollectionId !== undefined ? CollectionId : favourites.value?.[0]!.Id

      await $rentals('/api/user-profiles/me/add-to-favourites-list', {
        method: 'POST',
        body: {
          RvId,
          CollectionId: collectionListId,
          ProfileId: userId.value,
        },
      })

      await getAndSetFavourites()
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Removes an RV from a collection.
   *
   * @param id The ID of the RV to remove from the collection
   * @returns A promise that resolves when the RV is removed from the collection
   */
  async function removeRvFromCollection(id: number) {
    if (!passesAllGuards()) {
      return
    }

    try {
      isLoading.value = true

      await $rentals('/api/user-profiles/me/remove-from-collection', {
        method: 'DELETE',
        query: {
          Id: id,
        },
      })

      await getAndSetFavourites()
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Creates a new collection and optionally adds an RV to it.
   *
   * @param collectionName The name of the collection to create
   * @returns A promise that resolves when the collection is created
   */
  async function addCollectionToFavourites(collectionName: string) {
    if (!passesAllGuards()) {
      return
    }

    try {
      isLoading.value = true

      const result = await $rentals('/api/user-profiles/me/create-new-collection', {
        method: 'POST',
        body: {
          CollectionName: collectionName,
          ProfileId: userId.value,
        },
      })

      await getAndSetFavourites()

      return result
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Updates a collection's name.
   */
  async function updateCollectionInFavourites({ collectionId, newCollectionName }: { collectionId: number, newCollectionName: string }) {
    if (!passesAllGuards()) {
      return
    }

    try {
      isLoading.value = true

      await $rentals('/api/user-profiles/me/update-collection', {
        method: 'PUT',
        body: {
          Id: collectionId,
          CollectionName: newCollectionName,
          ProfileId: userId.value,
        },
      })

      await getAndSetFavourites()
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Removes a collection.
   *
   * @param collectionId The ID of the collection to remove
   * @returns A promise that resolves when the collection is removed
   */
  async function removeCollectionFromFavourites(collectionId: number) {
    if (!passesAllGuards()) {
      return
    }

    try {
      isLoading.value = true

      await $rentals('/api/user-profiles/me/remove-collection', {
        method: 'DELETE',
        query: {
          Id: collectionId,
        },
      })

      await getAndSetFavourites()
    }
    catch (error) {
      $captureError(error)
    }
    finally {
      isLoading.value = false
    }
  }

  /**
   * Checks if an RV is favourited in any of the collections.
   *
   * @param rvId The ID of the RV to check if it is favourited
   * @returns True if the RV is favourited, false otherwise
   */
  function checkIfRvIsFavourited(rvId: string) {
    return favourites.value?.some((collection) => collection.Items?.some((item) => item.RVId === rvId))
  }

  /**
   * Finds the collection ID that the RV is favourited in.
   *
   * @param rvId The ID of the RV to find the collection ID for
   * @returns The collection ID that the RV is favourited in
   */
  function findCollectionIdByRvId(rvId: string) {
    return favourites.value.flatMap((item) => item.Items).find((item) => item?.RVId === rvId)?.Id
  }

  return {
    favourites,
    isLoading,

    getAndSetFavourites,
    resetFavourites,

    addCollectionToFavourites,
    updateCollectionInFavourites,
    removeCollectionFromFavourites,

    addRvToCollection,
    removeRvFromCollection,

    checkIfRvIsFavourited,
    findCollectionIdByRvId,
  }
}
